import { memo, useCallback, useEffect, useMemo, useState } from "react";

import { api, errMsg, getAccessToken } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { cn } from "../lib/utils.js";
import { currentAnchor, rangeFor, shiftAnchor, type HistoryPeriod } from "../lib/historyRange.js";
import { useTeamMemberDetail } from "../hooks/useTeamMemberDetail.js";
import { TeamMemberDetailPanel } from "./TeamMemberDetailPanel.js";
import { Input } from "./ui/input.js";
import { DropdownSelect } from "./DropdownButton.js";
import { SpecularButton } from "./ui/specular-button.js";
import { StatusChip } from "./ui/status-chip.js";
import {
  AttendanceLegend, TONE_BY_STATUS, cellTitle,
  type GridRow, type MonthlyGrid,
} from "./attendance/attendanceCells.js";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table.js";
import { Skeleton } from "./Skeleton.js";

interface OrgMeta {
  offices: Array<{ id: string; name: string }>;
  departments: Array<{ id: string; name: string; office?: { id: string; name: string } | null }>;
}

/** Periods this grid offers. Week is omitted — too short to be worth a matrix. */
type GridPeriod = Extract<HistoryPeriod, "month" | "quarter" | "year" | "custom">;

/**
 * A year of day columns would be 365 cells per row, so long ranges switch to one
 * column per month. Custom follows the same rule by span rather than by name.
 */
const MAX_DAY_COLUMNS = 100;

const PERIOD_OPTIONS = [
  { value: "month", label: "Month" },
  { value: "quarter", label: "Quarter" },
  { value: "year", label: "Year" },
  { value: "custom", label: "Custom" },
];

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100].map((n) => ({ value: String(n), label: String(n) }));

function granularityFor(period: GridPeriod, from: string, to: string): "day" | "month" {
  if (period === "year") return "month";
  const spanDays = (Date.parse(to) - Date.parse(from)) / 86_400_000;
  return spanDays > MAX_DAY_COLUMNS ? "month" : "day";
}

function monthColumnLabel(ym: string): string {
  const [y, m] = ym.split("-").map(Number);
  return new Date(Date.UTC(y!, m! - 1, 1)).toLocaleDateString("en-IN", { month: "short", year: "2-digit", timeZone: "UTC" });
}

/**
 * One employee's row. Memoised because the grid draws up to 100 of these at ~31 cells
 * each: without it, every keystroke in the search box or change of any unrelated state
 * re-runs cva and twMerge for several thousand cells. `row` and `days` are stable
 * object identities from the fetched payload, and `onSelect` is a stable setState.
 */
const GridRowView = memo(function GridRowView({
  row, days, cellClass, onSelect,
}: {
  row: GridRow;
  days: string[];
  cellClass: string;
  onSelect: (id: string) => void;
}) {
  return (
    <TableRow className="hover:bg-surface-50">
      {/* Sticky cells need an opaque background of their own -- they are
          transparent by default and would show the day columns through. */}
      <TableCell className="sticky left-0 z-10 bg-surface-0 font-medium">
        <button
          type="button"
          onClick={() => onSelect(row.id)}
          title={`${row.firstName} ${row.lastName} - open attendance detail`}
          className="w-[140px] truncate text-left text-brand-700 hover:underline"
        >
          {row.firstName} {row.lastName}
        </button>
      </TableCell>
      <TableCell className="sticky left-[156px] z-10 bg-surface-0 text-[12px] text-surface-500">
        <div className="w-[110px] truncate">{row.employeeCode}</div>
      </TableCell>
      {row.cells.map((c, i) => (
        <TableCell key={days[i]} className={cellClass}>
          <StatusChip tone={TONE_BY_STATUS[c.status]} marker={c.rejected} title={cellTitle(c, days[i]!)}>
            {c.label}
          </StatusChip>
        </TableCell>
      ))}
      <TableCell className="sticky right-0 z-10 bg-surface-0 px-2 text-center text-[12px] font-semibold">
        {row.totals.present}
      </TableCell>
    </TableRow>
  );
});

export function MonthlyAttendanceGrid({ weekStart }: { weekStart: string }) {
  const [period, setPeriod] = useState<GridPeriod>("month");
  const [anchor, setAnchor] = useState(() => currentAnchor("month"));
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
  const [officeId, setOfficeId] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  // One "which dropdown is open" state for the whole bar: opening one closes any other.
  const [openFilter, setOpenFilter] = useState<string | null>(null);

  const [orgMeta, setOrgMeta] = useState<OrgMeta>({ offices: [], departments: [] });
  const [grid, setGrid] = useState<MonthlyGrid | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  // Custom owns its two date inputs; every other period derives its range from the anchor.
  const derived = rangeFor(period === "custom" ? "month" : period, anchor);
  const from = period === "custom" ? customFrom : derived.from;
  const to = period === "custom" ? customTo : derived.to;
  const rangeLabel = period === "custom" ? "Custom range" : derived.label;
  const granularity = from && to ? granularityFor(period, from, to) : "day";
  const rangeReady = Boolean(from && to && from <= to);

  const member = useTeamMemberDetail(selectedMemberId, weekStart);

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    api.get<OrgMeta>("/employees/org-meta")
      .then((r) => setOrgMeta(r.data))
      .catch((err) => logError("MonthlyAttendanceGrid.orgMeta", err));
  }, []);

  // Departments are narrowed to the chosen office client-side, as EmployeesPage does.
  const departments = useMemo(
    () => orgMeta.departments.filter((d) => !officeId || d.office?.id === officeId),
    [orgMeta.departments, officeId],
  );

  const buildParams = useCallback((paginated: boolean) => {
    const p = new URLSearchParams({ from, to, granularity });
    if (officeId) p.set("officeId", officeId);
    if (departmentId) p.set("departmentId", departmentId);
    if (debouncedSearch) p.set("search", debouncedSearch);
    if (paginated) { p.set("page", String(page)); p.set("limit", String(limit)); }
    return p;
  }, [from, to, granularity, officeId, departmentId, debouncedSearch, page, limit]);

  useEffect(() => {
    // A half-filled custom range would 400; wait for both dates.
    if (!rangeReady) { setLoading(false); setGrid(null); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.get<MonthlyGrid>(`/attendance/monthly-grid?${buildParams(true)}`)
      .then((r) => { if (!cancelled) setGrid(r.data); })
      .catch((err) => {
        if (cancelled) return;
        logError("MonthlyAttendanceGrid.load", err);
        setError(errMsg(err) ?? "Failed to load monthly attendance");
        setGrid(null);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [buildParams, rangeReady]);

  // A GET, so a plain Button with a busy flag rather than AsyncButton (which is for
  // mutating calls). Goes through fetch, not the axios instance, for the blob.
  async function exportWorkbook() {
    setExporting(true);
    setError(null);
    try {
      const res = await fetch(`/api/attendance/monthly-grid/export?${buildParams(false)}`, {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ""}` },
      });
      if (!res.ok) {
        // The 413 caps carry a "narrow your filter" message -- surface it rather
        // than letting the browser save the error JSON as a .xlsx.
        const body = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new Error(body?.error ?? `Export failed (${res.status})`);
      }
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement("a");
      a.href = url;
      a.download = `attendance-${from}-to-${to}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      logError("MonthlyAttendanceGrid.export", err);
      setError(err instanceof Error ? err.message : "Export failed");
    } finally {
      setExporting(false);
    }
  }

  const days = grid?.days ?? [];
  const totalPages = grid ? Math.max(1, Math.ceil(grid.total / grid.limit)) : 1;
  const byMonth = grid?.granularity === "month";
  // Month columns hold a count and a label like "Mar 26", so they need more room.
  const colWidth = byMonth ? "w-16" : "w-7";
  // Built once per render rather than per cell -- cn() runs twMerge, and this string
  // is identical for every one of the several hundred day cells.
  const cellClass = cn("px-0 py-1 text-center", colWidth);
  // How many columns the answer will have, known from the range alone: one per day,
  // or one per month at month granularity. Lets the skeleton match the real width.
  const skeletonColumns = useMemo(() => {
    if (!rangeReady) return byMonth ? 1 : 31;
    const spanDays = Math.round((Date.parse(to) - Date.parse(from)) / 86_400_000) + 1;
    if (!byMonth) return spanDays;
    const [fy, fm] = [Number(from.slice(0, 4)), Number(from.slice(5, 7))];
    const [ty, tm] = [Number(to.slice(0, 4)), Number(to.slice(5, 7))];
    return (ty - fy) * 12 + (tm - fm) + 1;
  }, [rangeReady, byMonth, from, to]);

  // Clicking a name opens that person's approval / weekly detail in place, replacing
  // the grid -- the same panel the removed department drill-down used to reach.
  if (selectedMemberId) {
    return member.memberDetail ? (
      <div className="px-8 pb-10">
        <TeamMemberDetailPanel
          memberDetail={member.memberDetail}
          memberWeeklyCorrections={member.memberWeeklyCorrections}
          memberCorrections={member.memberCorrections}
          memberOvertime={member.memberOvertime}
          memberShiftDayRequests={member.memberShiftDayRequests}
          memberHistory={member.memberHistory}
          onRefresh={member.reload}
          onBack={() => setSelectedMemberId(null)}
          backLabel="Back to All Team"
        />
      </div>
    ) : (
      <div className="flex flex-col gap-3 px-8 pb-10">
        <Skeleton width={180} height={18} />
        <Skeleton width="100%" height={120} />
        <Skeleton width="100%" height={160} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 px-8 pb-10">
      <AttendanceLegend>
        {/* Shares the legend row rather than taking one of its own. */}
        <SpecularButton onClick={() => void exportWorkbook()} disabled={exporting} size="sm" className="ml-auto">
          {exporting ? "Exporting..." : "Export Excel"}
        </SpecularButton>
      </AttendanceLegend>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <DropdownSelect
          value={officeId}
          onChange={(v) => { setOfficeId(v); setDepartmentId(""); setPage(1); }}
          placeholder="All Offices"
          options={orgMeta.offices.map((o) => ({ value: o.id, label: o.name }))}
          open={openFilter === "office"}
          onToggle={() => setOpenFilter((f) => (f === "office" ? null : "office"))}
          onClose={() => setOpenFilter(null)}
        />

        <DropdownSelect
          value={departmentId}
          onChange={(v) => { setDepartmentId(v); setPage(1); }}
          placeholder="All Departments"
          options={departments.map((d) => ({ value: d.id, label: d.name }))}
          open={openFilter === "dept"}
          onToggle={() => setOpenFilter((f) => (f === "dept" ? null : "dept"))}
          onClose={() => setOpenFilter(null)}
        />

        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or employee code..."
          className="w-56"
        />

        <DropdownSelect
          value={period}
          onChange={(v) => {
            const next = v as GridPeriod;
            setPeriod(next);
            if (next !== "custom") setAnchor(currentAnchor(next));
            setPage(1);
          }}
          placeholder="Period"
          options={PERIOD_OPTIONS}
          clearable={false}
          ariaLabel="Period"
          open={openFilter === "period"}
          onToggle={() => setOpenFilter((f) => (f === "period" ? null : "period"))}
          onClose={() => setOpenFilter(null)}
        />

        {period === "custom" ? (
          <div className="flex items-center gap-2">
            <Input type="date" value={customFrom} onChange={(e) => { setCustomFrom(e.target.value); setPage(1); }} className="w-auto" aria-label="From date" />
            <span className="text-surface-500">&rarr;</span>
            <Input type="date" value={customTo} onChange={(e) => { setCustomTo(e.target.value); setPage(1); }} className="w-auto" aria-label="To date" />
          </div>
        ) : (
        <div className="flex items-center gap-3">
          <SpecularButton onClick={() => { setAnchor((a) => shiftAnchor(period, a, -1)); setPage(1); }} aria-label="Previous period" className="w-8 px-0">&larr;</SpecularButton>
          <div className="min-w-[140px] text-center text-sm font-semibold">{rangeLabel}</div>
          <SpecularButton onClick={() => { setAnchor((a) => shiftAnchor(period, a, 1)); setPage(1); }} aria-label="Next period" className="w-8 px-0">&rarr;</SpecularButton>
        </div>
        )}
      </div>

      {error && (
        <div className="rounded-md bg-[var(--color-error-bg)] px-3 py-2 text-[13px] text-[var(--color-error-fg)]">{error}</div>
      )}

      {/* Grid */}
      <div className="overflow-x-auto rounded-md border border-surface-200">
        {loading ? (
          // The real table with chip-sized blocks in it: same column widths, same row
          // height, so the grid does not jump when the data lands. The column count
          // comes from the chosen range, which is known before the request returns.
          <Table className="w-max min-w-full">
            <TableHeader>
              <TableRow className="bg-surface-50">
                <TableHead className="sticky left-0 z-10 bg-surface-50"><div className="w-[140px] truncate">Emp Name</div></TableHead>
                <TableHead className="sticky left-[156px] z-10 bg-surface-50"><div className="w-[110px] truncate">Emp Id</div></TableHead>
                {Array.from({ length: skeletonColumns }).map((_, i) => (
                  <TableHead key={i} className={cn("px-0 text-center font-semibold", colWidth)}>&nbsp;</TableHead>
                ))}
                <TableHead className="sticky right-0 z-10 bg-surface-50 px-2 text-center">P</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from({ length: 8 }).map((_, r) => (
                <TableRow key={r}>
                  <TableCell className="sticky left-0 z-10 bg-surface-0"><Skeleton width={140} height={14} /></TableCell>
                  <TableCell className="sticky left-[156px] z-10 bg-surface-0"><Skeleton width={110} height={14} /></TableCell>
                  {Array.from({ length: skeletonColumns }).map((_, i) => (
                    <TableCell key={i} className={cellClass}>
                      <Skeleton width={byMonth ? 56 : 24} height={24} borderRadius="4px" style={{ margin: "0 auto" }} />
                    </TableCell>
                  ))}
                  <TableCell className="sticky right-0 z-10 bg-surface-0 px-2"><Skeleton width={14} height={14} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : !grid || grid.rows.length === 0 ? (
          <div className="p-10 text-center text-[13px] text-surface-500">
            No employees match these filters for {rangeLabel}.
          </div>
        ) : (
          <Table className="w-max min-w-full">
            <TableHeader>
              <TableRow className="bg-surface-50">
                {/* Two things keep the day columns at full size instead of being squeezed:
                    the table is w-max (under w-auto the browser compresses it into the
                    viewport and the days collapse), and the identity columns are sized by
                    an inner fixed-width div rather than by the cell -- with
                    table-layout:auto a td's max-width is advisory and the column still
                    stretches to its content. Emp Id's sticky offset is the sum of the
                    widths before it, each plus its px-2 padding. */}
                <TableHead className="sticky left-0 z-10 bg-surface-50">
                  <div className="w-[140px] truncate">Emp Name</div>
                </TableHead>
                <TableHead className="sticky left-[156px] z-10 bg-surface-50">
                  <div className="w-[110px] truncate">Emp Id</div>
                </TableHead>
                {days.map((d) => (
                  <TableHead key={d} className={cn("px-0 text-center font-semibold", colWidth)}>
                    {byMonth ? monthColumnLabel(d) : d.slice(8)}
                  </TableHead>
                ))}
                <TableHead className="sticky right-0 z-10 bg-surface-50 px-2 text-center">P</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {grid.rows.map((r) => (
                <GridRowView key={r.id} row={r} days={days} cellClass={cellClass} onSelect={setSelectedMemberId} />
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Pagination */}
      {grid && grid.rows.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 text-[13px] text-surface-600">
          <span className="flex items-center gap-2">
            Show
            <DropdownSelect
              value={String(limit)}
              onChange={(v) => { setLimit(Number(v)); setPage(1); }}
              placeholder="Entries per page"
              options={PAGE_SIZE_OPTIONS}
              clearable={false}
              ariaLabel="Entries per page"
              open={openFilter === "limit"}
              onToggle={() => setOpenFilter((f) => (f === "limit" ? null : "limit"))}
              onClose={() => setOpenFilter(null)}
            />
            entries
          </span>
          <span className="ml-auto">
            {(grid.page - 1) * grid.limit + 1}&ndash;{Math.min(grid.page * grid.limit, grid.total)} of {grid.total}
          </span>
          <SpecularButton onClick={() => setPage((p) => Math.max(1, p - 1))} aria-label="Previous page" className="w-8 px-0">&larr;</SpecularButton>
          <span>Page {grid.page} / {totalPages}</span>
          <SpecularButton onClick={() => setPage((p) => Math.min(totalPages, p + 1))} aria-label="Next page" className="w-8 px-0">&rarr;</SpecularButton>
        </div>
      )}
    </div>
  );
}
